<script lang="ts" setup>
	import { computed, onBeforeUnmount, onMounted, ref } from "vue";
	import { storeToRefs } from "pinia";
	import { PanelLeftClose, PanelLeftOpen } from "lucide-vue-next";
	import { useNavItems } from "@/composables/useNavItems";
	import { useUser } from "@/stores/user";
	import { getDefaultRoute } from "@/utils/defaultRoute";

	const { user } = storeToRefs(useUser());

	// The destinations, their gating and count badges come from one
	// composable shared with the phone tab bar and menu sheet.
	const { items, activeId, ensureLoaded } = useNavItems();

	const homeRoute = computed(() => getDefaultRoute(user.value));

	// The user's collapse choice lives in a cookie rather than localStorage so the
	// server renders the same width the client will, with no post-hydration jump.
	const collapsedPreference = useCookie<boolean>("leadx_nav_collapsed", {
		default: () => false,
		maxAge: 60 * 60 * 24 * 365,
		sameSite: "lax",
	});

	// Below the desktop breakpoint the sidebar is always a rail; the CSS enforces
	// that on its own, this flag just lets the template swap the logo and hide the
	// toggle to match. Defaults to false so SSR and first client render agree.
	const isNarrow = ref(false);
	let narrowQuery: MediaQueryList | null = null;
	const onNarrowChange = (event: MediaQueryListEvent | MediaQueryList) => {
		isNarrow.value = event.matches;
	};

	onMounted(() => {
		ensureLoaded();
		narrowQuery = window.matchMedia("(max-width: 1279px)");
		onNarrowChange(narrowQuery);
		narrowQuery.addEventListener("change", onNarrowChange);
	});

	onBeforeUnmount(() => {
		narrowQuery?.removeEventListener("change", onNarrowChange);
	});

	const isRail = computed(() => collapsedPreference.value || isNarrow.value);

	function toggleCollapsed() {
		collapsedPreference.value = !collapsedPreference.value;
	}
</script>

<template>
	<nav class="navigation" :class="{ rail: isRail }" aria-label="Main">
		<div class="brand">
			<NuxtLink :to="homeRoute" class="brand-link" aria-label="LeadX home">
				<Logo :variant="isRail ? 'mark' : 'wordmark'" :registered="true" />
			</NuxtLink>
		</div>

		<ul class="items">
			<li v-for="item in items" :key="item.id">
				<NuxtLink
					:to="item.to"
					class="item"
					:class="{ active: item.id === activeId }"
					:aria-current="item.id === activeId ? 'page' : undefined"
					:aria-label="item.badgeAriaLabel || item.label"
				>
					<span class="icon-wrap">
						<component :is="item.icon" :size="20" class="nav-icon" aria-hidden="true" />
						<span v-if="item.badge" class="dot" aria-hidden="true"></span>
					</span>
					<span class="label">{{ item.label }}</span>
					<span v-if="item.badge" class="badge" aria-hidden="true">{{ item.badge }}</span>
					<span class="tip" aria-hidden="true">{{ item.label }}</span>
				</NuxtLink>
			</li>
		</ul>

		<div class="foot" v-if="!isNarrow">
			<button type="button" class="item toggle" @click="toggleCollapsed" :aria-expanded="!collapsedPreference" :aria-label="collapsedPreference ? 'Expand navigation' : 'Collapse navigation'">
				<span class="icon-wrap">
					<PanelLeftOpen v-if="collapsedPreference" :size="20" class="nav-icon" aria-hidden="true" />
					<PanelLeftClose v-else :size="20" class="nav-icon" aria-hidden="true" />
				</span>
				<span class="label">Collapse</span>
				<span class="tip" aria-hidden="true">Expand</span>
			</button>
		</div>
	</nav>
</template>

<style scoped lang="scss">
	$nav-width: 220px;
	$rail-width: 64px;

	// Everything that changes when the sidebar becomes an icon rail lives here so
	// the user's toggle and the narrow-viewport rule can't drift apart.
	@mixin rail-state {
		width: $rail-width;

		.brand {
			padding: 0;
			justify-content: center;

			// The X is wider than it is tall, so it is sized by height and allowed
			// to run wider than the 20px icon column below it.
			.brand-link :deep(svg) {
				height: 22px;
			}
		}

		.items {
			padding: 12px 10px;
		}

		.foot {
			padding: 10px;
		}

		.item {
			justify-content: center;
			padding: 0;
			gap: 0;

			.label,
			.badge {
				display: none;
			}

			.dot {
				display: block;
			}

			// A tapped rail item keeps :hover on touch screens, which pinned the
			// label tooltip over the page (see the "UCC" chip in the mobile bug
			// report). Only pointers that can actually leave the item get it.
			@include hover-capable {
				&:hover .tip,
				&:focus-visible .tip {
					opacity: 1;
					transform: translate(0, -50%);
				}
			}
		}
	}

	.navigation {
		position: relative;
		z-index: 20;
		user-select: none;
		display: flex;
		flex-direction: column;
		flex-shrink: 0;
		width: $nav-width;
		background: var(--bg-surface);
		border-right: 1px solid var(--border);
		transition: width var(--dur) var(--ease-out);

		&.rail {
			@include rail-state;
		}

		@include touch {
			@include rail-state;
		}

		// Phones get the bottom tab bar (MobileTabBar.vue) instead of a rail.
		@include phone {
			display: none;
		}

		@media (prefers-reduced-motion: reduce) {
			transition: none;
		}
	}

	.brand {
		display: flex;
		align-items: center;
		flex-shrink: 0;
		height: var(--header-height);
		// 24px lines the wordmark's left edge up with the icon column below it.
		padding: 0 24px;
		border-bottom: 1px solid var(--border);

		.brand-link {
			display: flex;
			align-items: center;
			border-radius: 8px;

			:deep(svg) {
				display: block;
				height: 20px;
				width: auto;
			}
		}
	}

	.items {
		display: flex;
		flex-direction: column;
		gap: 2px;
		flex: 1 1 auto;
		min-height: 0;
		padding: 12px;
		margin: 0;
		list-style: none;

		li {
			position: relative;
		}
	}

	.foot {
		flex-shrink: 0;
		padding: 10px 12px;
		border-top: 1px solid var(--border);

		.toggle {
			position: relative;
		}
	}

	.item {
		position: relative;
		display: flex;
		align-items: center;
		gap: 12px;
		width: 100%;
		height: 40px;
		padding: 0 12px;
		border: 0;
		border-radius: var(--radius-md);
		background: transparent;
		color: var(--text);
		text-align: left;
		text-decoration: none;
		cursor: pointer;
		transition:
			background var(--dur-fast) var(--ease-out),
			color var(--dur-fast) var(--ease-out);

		.icon-wrap {
			position: relative;
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			width: 20px;
			height: 20px;
		}

		.nav-icon {
			opacity: 0.7;
			transition: opacity 0.15s ease-out;
		}

		.label {
			@include GoogleSans(false);
			flex: 1 1 auto;
			min-width: 0;
			// Matches .account-rail-label in account.scss, except for line-height:
			// this row clips overflow to ellipsize long labels, so the line box has
			// to be tall enough to hold descenders — line-height: 1 cuts the "g".
			font-size: 15px;
			font-weight: 500;
			letter-spacing: -0.2px;
			line-height: 20px;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}

		.badge {
			@include GoogleSans(false);
			flex-shrink: 0;
			min-width: 20px;
			height: 20px;
			padding: 0 6px;
			border-radius: var(--radius-pill);
			background: var(--bg-muted);
			color: var(--text-secondary);
			font-size: 11px;
			font-weight: 600;
			line-height: 20px;
			text-align: center;
		}

		// Rail-only stand-in for the badge: a small marker on the icon.
		.dot {
			display: none;
			position: absolute;
			top: -3px;
			right: -3px;
			width: 8px;
			height: 8px;
			border-radius: 50%;
			background: var(--accent);
			box-shadow: 0 0 0 2px var(--bg-surface);
		}

		// Rail-only label, shown to the right of the icon on hover or focus.
		.tip {
			@include GoogleSans(false);
			position: absolute;
			top: 50%;
			left: calc(100% + 12px);
			z-index: 30;
			padding: 6px 10px;
			border-radius: var(--radius-md);
			background: var(--bg-inverse);
			color: var(--text-inverse);
			font-size: 13px;
			font-weight: 500;
			letter-spacing: -0.1px;
			line-height: 1;
			white-space: nowrap;
			opacity: 0;
			pointer-events: none;
			transform: translate(-4px, -50%);
			transition:
				opacity 0.15s ease-out,
				transform 0.15s ease-out;
		}

		&:hover:not(.active) {
			background: var(--bg-subtle);

			.nav-icon {
				opacity: 1;
			}
		}

		&.active {
			background: var(--accent-soft);
			color: var(--accent);

			.nav-icon {
				opacity: 1;
			}

			.badge {
				background: var(--accent-tint);
				color: var(--accent);
			}
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.item,
		.item .nav-icon,
		.item .tip {
			transition: none;
		}
	}
</style>
